/**
 * orderColumns.ts — single source of truth for the OrderListPage
 * column registry. Used by the table renderer + the Customize Table
 * popover + the `useOrderColumnPrefs` hook.
 *
 * ## Legacy parity map (US-58231 task 01)
 *
 * Source: `legacy/.../enums/filter.enum.ts#TableColumnFilter` +
 * `legacy/.../order-table.component.ts#getOrderedByName` +
 * `legacy/.../truspace-orders-data/.../orders.service.ts` sort switch.
 *
 * | V3 key            | Legacy slug     | AC label          | fixed | ssiOnly | defaultVisible |
 * |-------------------|-----------------|-------------------|-------|---------|----------------|
 * | property          | property        | Property          | no    | no      | yes (all roles) |
 * | address           | address         | Address           | yes   | no      | yes (all roles) |
 * | company           | company         | Company           | no    | yes     | yes (SSI only) |
 * | propertyDetails   | propertyDetails | Property Details  | no    | no      | yes (all roles) |
 * | orderId           | requestId       | Order ID          | no    | no      | yes (all roles) |
 * | serviceType       | serviceType     | Service Type      | yes   | no      | yes (all roles) |
 * | complexity        | —               | Complexity        | no    | yes     | yes (SSI only, FR-146) |
 * | orderedBy         | orderedBy       | Ordered By        | no    | no      | yes (all roles) |
 * | orderDate         | orderDate       | Order Date        | yes   | no      | yes (all roles) |
 * | startDate         | startDate       | Start Date        | no    | yes     | yes (SSI only) |
 * | dueDate           | dueDate         | Due Date          | no    | no      | yes (all roles) |
 * | assignedTo        | assignedTo      | Assigned To       | no    | yes     | yes (SSI only) |
 * | status            | status          | Status            | no    | no      | yes (all roles) |
 *
 * **Ordered By semantics (confirmed):** client contact on the order —
 * `request.firstName` / `request.lastName`, overlaid from `users` by request
 * email when that user exists (`RequestService.LoadRequestList` ~355–362).
 * Legacy: `getOrderedByName()` prefers `item.orderedBy` then falls back to
 * `item.firstName item.lastName`; sort `case 'orderBy'` uses `request.firstName`
 * only (V3 adds `lastName` tie-break). The list cell also shows `request.email`
 * under the name (FR-584) — the same address `LoadRequestList` uses for the
 * name overlay. Sort and visibility stay on the name.
 *
 * **Company column (FR-123, FR-562):** display, filter, search, and sort are
 * the building owner (`buildings.owner_id` → `companies.name`, v2
 * `item.building?.company.name`). The firm that placed the order stays on
 * Ordered By.
 *
 * **Wave 4 → V3 prefs key migration** (handled in `useOrderColumnPrefs.normalizePrefs`):
 * - `createdAt` → `orderDate`
 * - `assignee` → `assignedTo`
 * - Wave 4 `address` label "Property" conflated property+address → split into `property` + `address`
 *
 * **Out of AC scope (drop from popover):** `priority`, `closedAt` (Wave 4 extras).
 *
 * ## Pinned sort map (BE task 06 — `GET /api/orders?sort=` / `?sort=-field`)
 *
 * | Sort key          | Drizzle ORDER BY                                      | Join        |
 * |-------------------|-------------------------------------------------------|-------------|
 * | property          | request.buildingName                                  | —           |
 * | address           | request.propertyAddress                               | —           |
 * | company           | building owner (`buildings.owner_id` → `companies.name`) | subquery    |
 * | propertyDetails   | floorNumber NULLS LAST, suiteNumber, tenantName         | —           |
 * | orderId           | request.requestId                                     | —           |
 * | serviceType       | serviceTypes.serviceName                                | existing    |
 * | orderedBy         | request.lastName, request.firstName                     | —           |
 * | orderDate         | request.requestDate                                     | —           |
 * | startDate         | request.startDate NULLS LAST                          | —           |
 * | dueDate           | request.dueDate NULLS LAST                            | —           |
 * | assignedTo        | users.lastName NULLS LAST, users.firstName            | left join   |
 * | status            | requestStatuses.requestName                           | existing    |
 *
 * Secondary sort when primary ≠ orderDate: `desc(request.requestDate)`, then
 * `desc(request.requestId)`. Export ignores `sort` but honors `columns` (listing
 * layout). CSV also inserts legacy export-only columns next to Address
 * (Subscription), Property Details (Floor(s)/Suite(s)/Priority), and Ordered By
 * (Created by SSI) — see `order-export-columns.ts`. Unknown sort key → 422.
 *
 * Adding a new column:
 *   1. Add a `{ key, label, defaultVisible, fixed?, ssiOnly? }` row here.
 *   2. Add a render case for that key in OrderListPage.
 *   3. Extend `normalizePrefs` if existing prefs need a key migration.
 *
 * Order in this array IS the default column order; `useOrderColumnPrefs`
 * reconciles any new columns into existing user prefs by appending.
 */

/** AC-aligned column registry — order matches legacy `TableColumnFilter`. */
const ORDER_COLUMN_ROWS = [
  { key: 'property',        label: 'Property',         defaultVisible: true },
  { key: 'address',         label: 'Address',          defaultVisible: true, fixed: true },
  { key: 'company',         label: 'Company',          defaultVisible: true, ssiOnly: true },
  { key: 'propertyDetails', label: 'Property Details', defaultVisible: true },
  { key: 'orderId',         label: 'Order ID',         defaultVisible: true },
  { key: 'serviceType',     label: 'Service Type',     defaultVisible: true, fixed: true },
  { key: 'complexity',      label: 'Complexity',       defaultVisible: true, ssiOnly: true },
  { key: 'orderedBy',       label: 'Ordered By',       defaultVisible: true },
  { key: 'orderDate',       label: 'Order Date',       defaultVisible: true, fixed: true },
  { key: 'startDate',       label: 'Start Date',       defaultVisible: true, ssiOnly: true },
  { key: 'dueDate',         label: 'Due Date',         defaultVisible: true },
  { key: 'assignedTo',      label: 'Assigned To',      defaultVisible: true, ssiOnly: true },
  { key: 'status',          label: 'Status',           defaultVisible: true },
] as const;

export type OrderColumnKey = (typeof ORDER_COLUMN_ROWS)[number]['key'];

export type OrderColumnDef = {
  key: OrderColumnKey;
  label: string;
  defaultVisible: boolean;
  /** Always visible; checkbox disabled in popover (Address, Service Type, Order Date). */
  fixed?: boolean;
  /** Shown only to SSI roles in the popover (Company, Start Date, Assigned To). */
  ssiOnly?: boolean;
};

export const ORDER_COLUMNS: readonly OrderColumnDef[] = ORDER_COLUMN_ROWS;

/** Registry rows the current role may toggle in the Columns popover. */
export function columnsForRole(isSSI: boolean): readonly OrderColumnDef[] {
  return ORDER_COLUMNS.filter((col) => !col.ssiOnly || isSSI);
}

/** Keys that must always appear in `visible` regardless of saved prefs. */
export function fixedColumnKeys(): OrderColumnKey[] {
  return ORDER_COLUMNS.filter((col) => col.fixed).map((col) => col.key);
}
